package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.LayoutMode
import top.wcpe.allincore.ui.runtime.ir.IrInterpreter
import kotlin.jvm.JvmStatic
import kotlin.math.roundToInt

/**
 * 分页容器：子节点按「页」摆放，每页尺寸等于视窗；滚动偏移按整数页对齐（snap）。
 *
 * 可水平翻页（[ScrollDirection.HORIZONTAL]，轮播图）或垂直翻页（[ScrollDirection.VERTICAL]，
 * 全屏滑动，默认）。每个直接子节点是一页，由 [IrInterpreter] 在实例化时强制其尺寸占满视窗。
 *
 * 内容尺寸覆盖为 `视窗尺寸 * 页数`，故 [requiresContentMeasure] 返回 false。本阶段（Phase 1）
 * snap 为「硬对齐」：[setCurrentPage] / [nextPage] / [previousPage] 直接把滚动偏移跳到目标页边界；
 * 松手 soft-snap 与平滑动画留待后续阶段。
 *
 * 非 final：[CarouselView] 继承本类，复用页面摆放与 snap，叠加首尾循环。
 */
@Suppress("TooManyFunctions", "MagicNumber")
open class PageView protected constructor() : ScrollableContainer<PageView>() {

    /** 指示器位置。 */
    enum class IndicatorPosition {
        BOTTOM,
        TOP,
        LEFT,
        RIGHT,
    }

    private var currentPageField = 0
    private var showIndicatorFlag = false
    private var indicatorPositionField = IndicatorPosition.BOTTOM
    private var indicatorActiveColorField = 0xFF448AFF.toInt()
    private var indicatorInactiveColorField = 0x66FFFFFF

    init {
        // 垂直翻页默认列式、水平翻页行式；direction() 覆盖里同步。
        styleField.layout().mode = LayoutMode.COLUMN
    }

    override fun self(): PageView = this

    override fun direction(d: ScrollDirection): PageView {
        super.direction(d)
        styleField.layout().mode = if (direction() == ScrollDirection.HORIZONTAL) LayoutMode.ROW else LayoutMode.COLUMN
        return this
    }

    // -------- 页数 / 内容尺寸 --------

    /** 页数 = 直接子节点数量。 */
    fun pageCount(): Int = children().size

    override fun requiresContentMeasure(): Boolean = false

    override fun contentWidth(): Float =
        if (direction() == ScrollDirection.HORIZONTAL) viewportWidth() * pageCount() else viewportWidth()

    override fun contentHeight(): Float =
        if (direction() == ScrollDirection.VERTICAL) viewportHeight() * pageCount() else viewportHeight()

    // -------- 当前页 --------

    fun currentPage(): Int = currentPageField

    /** 跳转到指定页（clamp 到 [0, pageCount-1]），并把滚动偏移对齐到该页边界。 */
    fun setCurrentPage(page: Int): PageView {
        currentPageField = clampPage(page, pageCount())
        snapToCurrentPage()
        return this
    }

    /** 下一页；到末页时不前进（循环行为由 [CarouselView] 覆盖）。 */
    open fun nextPage(): PageView = setCurrentPage(currentPageField + 1)

    /** 上一页；到首页时不后退。 */
    open fun previousPage(): PageView = setCurrentPage(currentPageField - 1)

    /** 把滚动偏移对齐到当前页边界。 */
    protected open fun snapToCurrentPage() {
        if (direction() == ScrollDirection.HORIZONTAL) {
            scrollX(currentPageField * viewportWidth())
        } else {
            scrollY(currentPageField * viewportHeight())
        }
    }

    /**
     * 重写视窗宽 setter：viewport 从 0 → 非 0 这一帧，主动 re-snap 到 currentPage。
     *
     * 修复「DSL 写 currentPage = 1 但启动后仍显示第 1 页」：IrInterpreter 在 children 装入前先
     * applyProp(currentPage)，那时 viewportWidth=0 → snapToCurrentPage 把 scrollX 设为 1*0 = 0；
     * 之后 children 装完后再 setCurrentPage 时 children 已加入，clampPage 不会被截到 0，但 viewport
     * 仍是 0；直到首帧 render 才设 viewport。本方法在 viewport 首次变非 0 时立即 snapToCurrentPage，
     * 让 scrollX 跳到正确页边界。
     */
    override fun viewportWidth(w: Float): PageView {
        val wasZero = viewportWidth() == 0f
        super.viewportWidth(w)
        val shouldSnap = wasZero && w > 0f && direction() == ScrollDirection.HORIZONTAL && currentPageField > 0
        if (shouldSnap) {
            snapToCurrentPage()
        }
        return this
    }

    override fun viewportHeight(h: Float): PageView {
        val wasZero = viewportHeight() == 0f
        super.viewportHeight(h)
        val shouldSnap = wasZero && h > 0f && direction() == ScrollDirection.VERTICAL && currentPageField > 0
        if (shouldSnap) {
            snapToCurrentPage()
        }
        return this
    }

    /**
     * 拖拽中按当前 scroll 偏移反推最接近的页号，实时同步 [currentPage]。
     *
     * 修复「拖动跨页后页码不变」：历史实现只在 [setCurrentPage] 链路里更新 currentPage，用户拖拽走
     * `ScrollableContainer.applyDragEvent` 直接累加 scroll，从未反向同步页号 → 指示器永远停在初始页、
     * 监听 currentPage 的 state 也收不到变化。本方法在 onDrag 路径每帧调用，只更新字段不动 scroll，
     * 真正 snap 留到 [onDragEnd]。
     */
    private fun syncCurrentPageFromScroll() {
        val count = pageCount()
        if (count <= 0) {
            return
        }
        val pageSize = if (direction() == ScrollDirection.HORIZONTAL) viewportWidth() else viewportHeight()
        if (pageSize <= 0f) {
            return
        }
        val offset = if (direction() == ScrollDirection.HORIZONTAL) scrollX() else scrollY()
        currentPageField = clampPage((offset / pageSize).roundToInt(), count)
    }

    /**
     * 重写拖拽结束：先按当前 scroll 反推最近页，然后走 scrollTo 平滑对齐到该页。
     * smoothScroll=true 时走渐进动画，否则瞬移。比 ScrollableContainer 默认的「惯性 + 弹性」
     * 行为更适合分页：松手后直接 snap，不让用户看到半页停留。
     */
    override fun onDragEnd() {
        // 1) 反推最近页号
        syncCurrentPageFromScroll()
        // 2) 平滑 snap 到目标页边界（scrollTo 内部判 smoothScroll）
        snapScrollToCurrentPage()
        // 3) 清拖拽状态（复用父类）。super.onDragEnd 可能在 inertia=true 时启动 fling，
        //    与刚才的 scrollTo 冲突；PageView 语义是「分页 snap」，fling 与 snap 不应并存。
        super.onDragEnd()
        // 再次 scrollTo，让 ScrollAnimator 的 smoothTo 覆盖父类可能启动的 fling 状态。
        snapScrollToCurrentPage()
    }

    /** 沿当前方向平滑滚动到 currentPage 的页边界。 */
    private fun snapScrollToCurrentPage() {
        if (direction() == ScrollDirection.HORIZONTAL) {
            scrollTo(currentPageField * viewportWidth(), scrollY())
        } else {
            scrollTo(scrollX(), currentPageField * viewportHeight())
        }
    }

    // -------- 指示器 --------

    fun showIndicator(show: Boolean): PageView = apply { showIndicatorFlag = show }

    fun showIndicator(): Boolean = showIndicatorFlag

    fun indicatorPosition(pos: IndicatorPosition?): PageView = apply {
        indicatorPositionField = pos ?: IndicatorPosition.BOTTOM
    }

    fun indicatorPosition(): IndicatorPosition = indicatorPositionField

    fun indicatorActiveColor(argb: Int): PageView = apply { indicatorActiveColorField = argb }

    fun indicatorActiveColor(): Int = indicatorActiveColorField

    fun indicatorInactiveColor(argb: Int): PageView = apply { indicatorInactiveColorField = argb }

    fun indicatorInactiveColor(): Int = indicatorInactiveColorField

    companion object {
        @JvmStatic
        fun create(): PageView = PageView()

        /** 把页号 clamp 到 [0, count-1]；count<=0 时返回 0。 */
        private fun clampPage(page: Int, count: Int): Int = if (count <= 0) 0 else page.coerceIn(0, count - 1)
    }
}
